<template>
  <div class="job-architecture-list-view">
    <div class="job-architecture-table-header architecture-row-grid">
      <div class="text-body-2 text-textDarkest">
        Name
      </div>
      <div class="text-body-2 text-textDarkest">
        Job Families
      </div>
      <div class="text-body-2 text-textDarkest">
        Job Profiles
      </div>
      <div class="text-body-2 text-textDarkest">
        Employees
      </div>
      <div class="text-body-2 text-textDarkest architecture-header-alerts d-flex align-center ga-1">
        Alerts
        <div class="d-flex align-center ml-1" @click.stop>
          <InsightsTooltip :tooltip="alertsTooltip" tooltip-placement="right" />
        </div>
      </div>
    </div>
    <div>
      <ListFilterEmptyState
        v-if="showEmptyState"
        hide-bottom-border
        message="No items match your filters or search. Try adjusting your filters or search term."
      />
      <JobArchitectureTreeview
        v-else
        v-model:selected="selectedProfiles"
        :items="treeItems"
        item-value="value"
        item-title="title"
        item-children="children"
        selectable
      >
        <template #item-label="{ item }">
          {{ item.title }}
        </template>
      </JobArchitectureTreeview>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue';
import { storeToRefs } from 'pinia';
import {
  computeArchitectureAlerts,
  familyMapKey,
  getFamilyAlertTooltipContext,
  getGroupAlertTooltipContext,
  getProfileAlertTooltipContext,
} from '@/components/jobArchitecture/alertHelpers';
import {
  groupId,
  familyNodeId,
  getGroupArchitectureStats,
  getGroupTotalProfileCount,
  getFamilyArchitectureStats,
  formatArchitectureFamilyCountLabel,
  formatArchitectureProfilesCountLabel,
} from '@/components/jobArchitecture/helpers';
import JobArchitectureTreeview from '@/components/shared/JobArchitectureTreeview.vue';
import ListFilterEmptyState from '@/components/shared/ListFilterEmptyState.vue';
import InsightsTooltip from '@/components/insights/InsightsTooltip.vue';
import { getTooltip } from '@/helpers/tooltips';
import { useAppLoading } from '@/stores/app/loading';
import { useFilters } from '@/stores/filters/filters';
import { useJobArchitectureFilters } from '@/composables/useJobArchitectureFilters';
import { formatJobLevel } from '@/helpers/jobLevelDisplay';

const props = defineProps({
  treeData: { type: Object, default: null },
});

const selectedProfiles = defineModel('selectedProfiles', {
  type: Array,
  default: () => [],
});

const { pageLoading } = storeToRefs(useAppLoading());
const { areNonIgnorableFiltersApplied } = storeToRefs(useFilters());
const { searchText } = useJobArchitectureFilters();

const alertsTooltip = computed(() => getTooltip('architecture-alerts'));

const alertsData = computed(() => computeArchitectureAlerts(props.treeData));

const treeItems = computed(() => {
  const groups = props.treeData?.groups;

  if (!groups?.length) return [];

  const {
    byProfile,
    byFamily,
    byGroup,
    profileCounts,
    familyCounts,
  } = alertsData.value;
  const profileCountMap = profileCounts;
  const familyCountMap = familyCounts;

  return groups.map((group) => {
    const families = group.families || [];
    const groupStats = getGroupArchitectureStats(group);
    const profileTotal = getGroupTotalProfileCount(group);
    const groupNodeIdValue = groupId(group);

    return {
      title: group.name || 'Uncategorized',
      value: groupNodeIdValue,
      structureFamiliesLabel: formatArchitectureFamilyCountLabel(groupStats.familyCount),
      structureProfilesLabel: formatArchitectureProfilesCountLabel(profileTotal),
      employeesDisplay: groupStats.employeeCount,
      alerts: byGroup.get(groupNodeIdValue) ?? [],
      nodeType: 'group',
      alertTooltipContext: getGroupAlertTooltipContext(),
      children: families.map((family) => {
        const profiles = family.profiles || [];
        const famStats = getFamilyArchitectureStats(family);
        const profileLabel = `${famStats.profileCount} job profile${famStats.profileCount === 1 ? '' : 's'}`;
        const familyKey = familyMapKey(group, family);

        return {
          title: family.name || 'Uncategorized',
          value: familyNodeId(group, family),
          structureProfilesLabel: profileLabel,
          employeesDisplay: famStats.employeeCount,
          alerts: byFamily.get(familyKey) ?? [],
          nodeType: 'family',
          alertTooltipContext: getFamilyAlertTooltipContext(familyCountMap, familyKey),
          children: profiles.map((profile) => ({
            title: profile.title || '',
            value: profile.ulid,
            employeesDisplay: profile.employee_count || 0,
            jobLevel: formatJobLevel(profile.job_level, { mode: 'short' }) || null,
            jobCode: profile.hris_job_code,
            alerts: profile.ulid ? (byProfile.get(profile.ulid) ?? []) : [],
            nodeType: 'profile',
            alertTooltipContext: profile.ulid
              ? getProfileAlertTooltipContext(profileCountMap, profile.ulid)
              : { familyNames: [], groupNames: [] },
          })),
        };
      }),
    };
  });
});

const hasFiltersOrSearch = computed(() => (
  Boolean(searchText.value) || areNonIgnorableFiltersApplied.value
));

const showEmptyState = computed(() => (
  !pageLoading.value
  && treeItems.value.length === 0
  && hasFiltersOrSearch.value
));
</script>

<style scoped lang="less">
.job-architecture-list-view {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.architecture-row-grid {
  display: grid;
  align-items: center;
  grid-template-columns: minmax(0, 1fr) minmax(70px, 105px) minmax(80px, 120px) 88px minmax(160px, 185px);
  column-gap: 12px;
  padding-left: 43px;
  padding-right: 16px;
}

.job-architecture-table-header {
  position: sticky;
  top: 96px;
  z-index: 2;
  height: 40px;
  border-bottom: 1px solid @stroke-default;
  border-top: 1px solid @stroke-default;
  background-color: @bg-canvas;
  border-radius: 8px 8px 0 0;
}
.architecture-header-alerts {
  width: 185px;
  min-width: 160px;
  padding-left: 4px;
}
</style>
